import { useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { Combobox } from 'medere-ui-framework';
import { FieldError } from '../../../../../components/novarium/NvTableParts';
import { searchBillingConcepts } from '../../../services/AgendaService';
import { BillingConceptOption, OrderRowRequest } from '../../../types/billing.types';
import { newRowKey } from '../billingUtils';

interface OtherConceptsDialogProps {
  canViewCoverageAmount: boolean;
  onCancel: () => void;
  onAdd: (row: OrderRowRequest) => void;
}

type Mode = 'catalog' | 'free';

/**
 * "Agregar otros conceptos" ({@code BillingConceptSearchWindow}): un concepto de facturación del catálogo, o un
 * concepto sin código con su descripción e importes.
 */
export default function OtherConceptsDialog({ canViewCoverageAmount, onCancel, onAdd }: OtherConceptsDialogProps) {
  const [mode, setMode] = useState<Mode>('catalog');
  const [concept, setConcept] = useState<BillingConceptOption | null>(null);
  const [description, setDescription] = useState('');
  const [financier, setFinancier] = useState<number | null>(null);
  const [patient, setPatient] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ concept?: string; description?: string; amount?: string }>({});

  const add = () => {
    if (mode === 'catalog') {
      if (!concept) {
        setErrors({ concept: 'Elegí el concepto' });
        return;
      }
      onAdd({ rowKey: newRowKey('c'), billingConceptId: concept.id, quantity: 1 });
      return;
    }
    const next: typeof errors = {};
    if (!description.trim()) next.description = 'Completá la descripción';
    if (!((patient ?? 0) > 0 || (canViewCoverageAmount && (financier ?? 0) > 0))) next.amount = 'Ingresá un importe mayor a 0';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onAdd({
      rowKey: newRowKey('c'),
      conceptDescription: description.trim(),
      conceptFinancierAmount: canViewCoverageAmount ? financier ?? 0 : null,
      conceptPatientAmount: patient ?? 0,
      quantity: 1,
    });
  };

  return (
    <Dialog
      header="Agregar otros conceptos"
      visible
      onHide={onCancel}
      className="nv-scope nv-dialog"
      modal
      draggable={false}
      footer={(
        <>
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onCancel} />
          <Button type="button" className="buttonMain" label="Agregar" onClick={add} />
        </>
      )}
    >
      <div className="nv-dialog-body">
        <SelectButton
          value={mode}
          options={[{ label: 'Del catálogo', value: 'catalog' }, { label: 'Concepto sin código', value: 'free' }]}
          onChange={(e) => { if (e.value) { setMode(e.value); setErrors({}); } }}
        />
        {mode === 'catalog' ? (
          <div className="nv-form-row">
            <Combobox
              label="Concepto"
              required
              className="nv-w-l"
              getItems={searchBillingConcepts}
              optionLabel="name"
              placeholder="Buscar por código o descripción"
              value={concept?.name ?? ''}
              setValue={(value: BillingConceptOption | null) => { setConcept(value ?? null); setErrors({}); }}
              error={Boolean(errors.concept)}
              caption={errors.concept}
            />
          </div>
        ) : (
          <>
            <div className="nv-form-row">
              <div className="nv-field nv-w-l">
                <label htmlFor="concept-description">Descripción <span className="nv-req">*</span></label>
                <InputText
                  id="concept-description"
                  value={description}
                  maxLength={255}
                  onChange={(e) => { setDescription(e.target.value); setErrors((c) => ({ ...c, description: undefined })); }}
                  className={errors.description ? 'p-invalid' : undefined}
                />
                <FieldError id="concept-description-error" message={errors.description} />
              </div>
            </div>
            <div className="nv-form-row">
              {canViewCoverageAmount && (
                <div className="nv-field nv-w-s">
                  <label htmlFor="concept-financier">Importe OS</label>
                  <InputNumber inputId="concept-financier" value={financier} mode="currency" currency="ARS" locale="es-AR"
                    minFractionDigits={2} maxFractionDigits={2} min={0}
                    onValueChange={(e) => { setFinancier(e.value ?? null); setErrors((c) => ({ ...c, amount: undefined })); }}
                    className={errors.amount ? 'p-invalid' : undefined} />
                </div>
              )}
              <div className="nv-field nv-w-s">
                <label htmlFor="concept-patient">Importe paciente</label>
                <InputNumber inputId="concept-patient" value={patient} mode="currency" currency="ARS" locale="es-AR"
                  minFractionDigits={2} maxFractionDigits={2} min={0}
                  onValueChange={(e) => { setPatient(e.value ?? null); setErrors((c) => ({ ...c, amount: undefined })); }}
                  className={errors.amount ? 'p-invalid' : undefined} />
              </div>
            </div>
            <FieldError id="concept-amount-error" message={errors.amount} />
          </>
        )}
      </div>
    </Dialog>
  );
}
